import type { CPMResult, Phase, Task } from "./types";
import { completedDays, computeCPM, effectiveActualFinish, effectiveActualStart, milestoneDisplayDay, taskShiftsPerDay } from "./cpm";
import { DEFAULT_PHASES } from "./phases";
import type { PhaseDef } from "./phases";

/**
 * The management readout: when it finishes, whether that's moved, which landmarks are
 * left, what's driving the date, and what's stuck.
 *
 * Kept out of the component so the arithmetic behind the numbers someone reports
 * upward is testable.
 */

export interface MilestoneRow {
  task: Task;
  /** Project day it's forecast to land on. */
  day: number;
  /** Where the baseline plan put it, if a baseline could be computed. */
  baselineDay: number | null;
  /** Positive = later than baseline. */
  drift: number | null;
  complete: boolean;
  critical: boolean;
}

export interface BlockedRow {
  task: Task;
  /** Days the block has been standing, or null with no calendar / no recorded date. */
  age: number | null;
}

export interface PhaseProgress {
  phase: Phase;
  doneDays: number;
  totalDays: number;
}

export interface TaskVarianceRow {
  task: Task;
  baselineStart: number | null;
  baselineFinish: number | null;
  forecastStart: number | null;
  forecastFinish: number | null;
  actualStart: number | null;
  actualFinish: number | null;
  /** actual (or, once one exists, forecast) minus baseline. Positive = later than planned. */
  startVariance: number | null;
  finishVariance: number | null;
  status: "not started" | "in progress" | "complete";
}

export interface Summary {
  forecastEnd: number;
  baselineEnd: number | null;
  /** Working days the forecast has moved past the baseline. Positive = late. */
  drift: number | null;
  totalDays: number;
  doneDays: number;
  percentDone: number;
  milestones: MilestoneRow[];
  criticalPath: Task[];
  criticalDays: number;
  blocked: BlockedRow[];
  phases: PhaseProgress[];
  taskVariance: TaskVarianceRow[];
}

/** Planned duration of a task under the deterministic estimate, in calendar days. */
function plannedDays(t: Task, projectShiftsPerDay: 1 | 2 = 1): number {
  if (t.type === "milestone") return 0;
  return Math.max(0, Number(t.m) || 0) / taskShiftsPerDay(t, projectShiftsPerDay);
}

/**
 * The plan as drawn: baseline durations, the same dependency network and pins, but no
 * progress and no today-floor. Comparing the live schedule against it is what isolates
 * the movement caused by how the job has actually gone.
 */
export function baselineSchedule(tasks: Task[], allowWeekendWork: boolean, shiftsPerDay: 1 | 2 = 1): CPMResult | null {
  const asBaseline = tasks.map((t) => ({
    ...t,
    m: t.type === "milestone" ? 0 : Math.max(0, Number(t.baseline) || 0),
    doneDays: [],
  }));
  const r = computeCPM(asBaseline, "m", allowWeekendWork, { shiftsPerDay });
  return r.ok ? r : null;
}

/**
 * Per-task baseline vs. forecast vs. actual start/finish, for a task-level readout of
 * how execution has actually gone rather than just the milestone/phase rollups above.
 * Variance compares the actual date once one exists, falling back to the forecast date
 * for work that hasn't started or finished yet — so an in-progress task still shows how
 * its current placement is tracking against the baseline.
 *
 * CPM's EF is an exclusive boundary (work finishes at the *end* of the day before it —
 * see `lastDay` in SummaryView), while `actualFinish` is the last day actually worked,
 * inclusive. Every finish field here is normalized to that inclusive convention so they
 * compare directly with no off-by-one.
 */
export function buildTaskVariance(
  tasks: Task[],
  result: CPMResult,
  baseline: CPMResult | null,
  shiftsPerDay: 1 | 2 = 1
): TaskVarianceRow[] {
  return tasks
    .filter((t) => t.type !== "milestone" && result.ES[t.id] !== undefined)
    .map((t) => {
      const baselineStart = baseline?.ES[t.id] ?? null;
      const baselineFinish = baseline?.EF[t.id] != null ? baseline!.EF[t.id] - 1 : null;
      const forecastStart = result.ES[t.id] ?? null;
      const forecastFinish = result.EF[t.id] != null ? result.EF[t.id] - 1 : null;
      const actualStart = effectiveActualStart(t);
      const actualFinish = effectiveActualFinish(t, shiftsPerDay);
      const status: TaskVarianceRow["status"] = result.complete[t.id] || actualFinish != null
        ? "complete"
        : actualStart != null
        ? "in progress"
        : "not started";
      const compareStart = actualStart ?? forecastStart;
      const compareFinish = actualFinish ?? forecastFinish;
      return {
        task: t,
        baselineStart,
        baselineFinish,
        forecastStart,
        forecastFinish,
        actualStart,
        actualFinish,
        startVariance: baselineStart == null || compareStart == null ? null : compareStart - baselineStart,
        finishVariance: baselineFinish == null || compareFinish == null ? null : compareFinish - baselineFinish,
        status,
      };
    });
}

export function buildSummary(
  tasks: Task[],
  result: CPMResult,
  allowWeekendWork: boolean,
  today: number | null,
  phases: PhaseDef[] = DEFAULT_PHASES,
  shiftsPerDay: 1 | 2 = 1
): Summary {
  const baseline = baselineSchedule(tasks, allowWeekendWork, shiftsPerDay);

  const milestones: MilestoneRow[] = tasks
    .filter((t) => t.type === "milestone" && result.ES[t.id] !== undefined)
    .map((t) => {
      const day = Math.max(0, milestoneDisplayDay(t, result.ES, result.EF, allowWeekendWork));
      const baselineDay = baseline
        ? Math.max(0, milestoneDisplayDay(t, baseline.ES, baseline.EF, allowWeekendWork))
        : null;
      return {
        task: t,
        day,
        baselineDay,
        drift: baselineDay == null ? null : day - baselineDay,
        complete: !!result.complete[t.id],
        critical: !!result.critical[t.id],
      };
    })
    .sort((a, b) => a.day - b.day);

  // Ordered by when each one runs, which is how the chain reads to someone being
  // told "these are the activities holding the end date".
  const criticalPath = tasks
    .filter((t) => result.critical[t.id])
    .sort((a, b) => (result.ES[a.id] ?? 0) - (result.ES[b.id] ?? 0));

  const activities = tasks.filter((t) => t.type !== "milestone");
  const totalDays = activities.reduce((n, t) => n + plannedDays(t, shiftsPerDay), 0);
  const doneDays = activities.reduce((n, t) => n + Math.min(completedDays(t).length, plannedDays(t, shiftsPerDay)), 0);

  const blocked: BlockedRow[] = tasks
    .filter((t) => t.blocked)
    .map((t) => ({
      task: t,
      age: today != null && t.blockedSince != null ? Math.max(0, today - t.blockedSince) : null,
    }))
    // Oldest first: a block that's been standing a fortnight is the one to talk about.
    .sort((a, b) => (b.age ?? -1) - (a.age ?? -1));

  const phaseProgress: PhaseProgress[] = phases.map(({ name: phase }) => {
    const inPhase = activities.filter((t) => t.phase === phase);
    return {
      phase,
      totalDays: inPhase.reduce((n, t) => n + plannedDays(t, shiftsPerDay), 0),
      doneDays: inPhase.reduce((n, t) => n + Math.min(completedDays(t).length, plannedDays(t, shiftsPerDay)), 0),
    };
  }).filter((p) => p.totalDays > 0);

  const forecastEnd = Math.ceil(result.projectEnd - 1e-9);
  const baselineEnd = baseline ? Math.ceil(baseline.projectEnd - 1e-9) : null;

  return {
    forecastEnd,
    baselineEnd,
    drift: baselineEnd == null ? null : forecastEnd - baselineEnd,
    totalDays,
    doneDays,
    percentDone: totalDays > 0 ? Math.round((doneDays / totalDays) * 100) : 0,
    milestones,
    criticalPath,
    criticalDays: criticalPath.reduce((n, t) => n + plannedDays(t, shiftsPerDay), 0),
    blocked,
    phases: phaseProgress,
    taskVariance: buildTaskVariance(tasks, result, baseline, shiftsPerDay),
  };
}
